import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { REGION_COLORS, colorForBereich } from '../data/regionColors.js';
import { foldedIncludes, grapeMatchesQuery, grapeDisplay, grapeKind } from '../data/grapes.js';
import { bereicheFor } from '../data/regionsAndBereiche.js';
import { supabase } from '../lib/supabase.js';
import { isMobile } from '../utils/isMobile.js';
import HelpContent, { HELP_TITLE } from './HelpContent.jsx';
import ImpressumContent from './ImpressumContent.jsx';
import { WarnIcon, BottleIcon, VineIcon, HouseIcon, ExitIcon, EyeIcon, EyeOffIcon, FilterIcon, StarIcon } from './icons.jsx';
import Tip from './Tip.jsx';
import { BEREICH_STATS, REGION_STATS } from '../data/bereichStats.js';
import { isFavoriteProducer } from '../data/producerTypes.js';
import { ATLAS } from '../data/atlasConfig.js';
import { REGION_CONTENT } from '../data/regionContent.js';
import { COMPUTED_AREAS } from '../data/computedAreas.js';

const PRODUCER_DISCLAIMER = "This producer list is Mike's curated list and not representative of any wine program study material. There are no, and never will be, affiliate relationships influencing the producers shown.";

function FrankenModal({ onClose }) {
  return createPortal(
    <div className="help-overlay" onClick={onClose}>
      <div className="help-modal help-modal--sticky" onClick={e => e.stopPropagation()}>
        <div className="help-modal__sticky-header">
          <h2 className="help-modal__title">Franken's Bereiche</h2>
          <button className="help-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="help-modal__scroll">
        <div className="help-modal__section">
          <p>If you study Franken with WSG, WSET, or MW materials, you may see references to 3 traditional Bereiche: Mainviereck, Maindreieck, and Steigerwald. These map cleanly to three geological substrates from the Triassic period.</p>
          <p>If you look at this map or any post-2017 German source, you'll see 12 official Bereiche. This map shows the 12 because they're the legally registered Bereiche in the Bayerische Weinbergsrolle, the Bavarian vineyard register.</p>
          <p><strong>Both are correct. They answer different questions.</strong></p>
        </div>

        <div className="help-modal__section">
          <h3>The three traditional Bereiche</h3>
          <p>These align with the three Triassic geological layers that define Franken's terroir, running west to east as the Main river cuts through them.</p>
          <p><strong>Mainviereck</strong> is the western Main quadrangle from Aschaffenburg to Wertheim, defined by Buntsandstein (red sandstone). The warm soils retain heat overnight, and the bayerischer Untermain is Franken's red wine heartland. Spätburgunder and Frühburgunder from Bürgstadter Centgrafenberg and Klingenberger Schlossberg are the iconic sites.</p>
          <p><strong>Maindreieck</strong> is the central Main triangle that loops through Würzburg, defined by Muschelkalk (shell limestone) from a 240-million-year-old shallow sea. The largest and most prestigious Bereich, with structure, salinity, and slow ripening as its hallmarks. Würzburger Stein and Escherndorfer Lump sit here. Silvaner is the signature.</p>
          <p><strong>Steigerwald</strong> is the east, around Iphofen and Castell, defined by Keuper (gypsum-marl). Heavy soils retain moisture and produce powerful, structured Silvaner with weight and density. Iphöfer Julius-Echter-Berg, Castell and Iphofen are the benchmark sites.</p>
        </div>

        <div className="help-modal__section">
          <h3>Why 12 now</h3>
          <p>In early 2017, the Bavarian wine authorities subdivided the three traditional Bereiche into twelve new ones. The motivation was tourism marketing rather than geology. Names like Weinpanorama Steigerwald and Mittelfränkische Bocksbeutelstraße were created to give individual wine-tourism regions distinct identities. The new Bereiche sometimes cut through coherent landscapes and don't track historical or geological boundaries.</p>
          <p>The 12 are the names you'll see on modern wine labels and in the Bavarian Weinbergsrolle.</p>
        </div>

        <div className="help-modal__section">
          <h3>How the EU registers it</h3>
          <p>The legal source of truth is the Produktspezifikation für die geschützte Ursprungsbezeichnung Franken (PDO file PDO-DE-A1267-AM01), published in the EU Official Journal on 30 December 2025 and binding from harvest 2026.</p>
          <p>The Produktspezifikation defers entirely to the Bavarian Weinbergsrolle, which defines the Bereiche:</p>
          <p style={{ fontStyle: 'italic', color: '#57534e', fontSize: '0.75rem' }}>"Die Bayerische Weinbergsrolle stellt das Verzeichnis der für die kleineren geografischen Einheiten zugelassenen Namen von Bereichen, Groß- und Einzellagen sowie Gewannen dar."</p>
        </div>

        <div className="help-modal__section">
          <h3>Bereich vs. Großlage on labels</h3>
          <p>Under the new §39 Weinverordnung, in force since 2021 and fully binding from harvest 2026, both Bereich and Großlage names must be prefixed with "Region" on wine labels. A wine from Großlage Iphöfer Burgweg labels as Region Burgweg.</p>
          <p>This was a deliberate consumer-protection move. Großlagen had become misleading: a name like Niersteiner Gutes Domtal suggested premium origin from Nierstein but actually covered a vast generic area.</p>
        </div>

        <div className="help-modal__section">
          <h3>What this map shows</h3>
          <p>The 12 official Bereiche from the Bayerische Weinbergsrolle. For exam preparation and understanding terroir, return to the 3 traditional Bereiche. The geological story (Buntsandstein, Muschelkalk, Keuper from west to east) is what determines wine character. The 12 modern Bereiche are administrative overlays on top of that.</p>
        </div>

        <div className="help-modal__section">
          <h3>Sources</h3>
          <ul className="help-modal__links">
            <li><a href="https://eur-lex.europa.eu/eli/C/2025/6794/oj" target="_blank" rel="noopener noreferrer">EU Produktspezifikation</a></li>
            <li><a href="https://www.lwg.bayern.de/weinbau/" target="_blank" rel="noopener noreferrer">Bayerische Landesanstalt für Weinbau</a></li>
            <li><a href="https://de.wikipedia.org/wiki/Franken_(Weinanbaugebiet)" target="_blank" rel="noopener noreferrer">Wikipedia — Franken</a></li>
            <li><a href="https://www.bwv-net.de/weinbau/weinbezeichnungsrecht-je-kleiner-desto-groesser/" target="_blank" rel="noopener noreferrer">BWV — §39 labeling rules</a></li>
            <li><a href="https://www.deutscheweine.de/anbaugebiet/66/franken" target="_blank" rel="noopener noreferrer">Deutsches Weininstitut — Franken</a></li>
            <li><a href="https://www.historisches-lexikon-bayerns.de/Lexikon/Weinbau_in_Franken" target="_blank" rel="noopener noreferrer">Historisches Lexikon Bayerns — Weinbau in Franken</a></li>
          </ul>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function WurttembergModal({ onClose }) {
  return createPortal(
    <div className="help-overlay" onClick={onClose}>
      <div className="help-modal help-modal--sticky" onClick={e => e.stopPropagation()}>
        <div className="help-modal__sticky-header">
          <h2 className="help-modal__title">Württemberg's Bereiche</h2>
          <button className="help-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="help-modal__scroll">
        <div className="help-modal__section">
          <p>In June 2025, Württemberg renamed two of its six Bereiche as part of a broader overhaul of the region's geographical designations under the Weinbergslagenverordnung. The updated Produktspezifikation and boundary delimitation for the g.U. Württemberg were published on 6 June 2025.</p>
          <p>This map shows the new, legally current names. Older books, maps, and course materials still use the pre-2025 names.</p>
        </div>

        <div className="help-modal__section">
          <h3>What was renamed</h3>
          <p>Remstal-Stuttgart → Remstal. The state capital was dropped from the name; the Bereich now carries the established name of the Rems valley landscape.</p>
          <p>Oberer Neckar → Neckar-Alb. Renamed after the Neckar-Alb landscape between the river and the Swabian Alb. The Großlage Hohenneuffen was assigned to this Bereich in the same reform.</p>
          <p>The other four Bereiche are unchanged: Württembergisch Unterland, Kocher-Jagst-Tauber, Württembergischer Bodensee, and Bayerischer Bodensee.</p>
          <p>Below Bereich level, some Großlagen changed too: Salzberg was renamed Breitenauer See, and Kocherberg and Lindelberg were combined under the name Hohenlohe.</p>
        </div>

        <div className="help-modal__section">
          <h3>Why</h3>
          <p>The renamings are part of Württemberg's shift to origin-based profiling following the Romance model, with a VDP-style quality pyramid that is binding from vintage 2026. Decisions by the Schutzgemeinschaft Württemberg required the changes to be in place from August 2025. The new names favor landscapes consumers actually recognize over administrative constructions.</p>
        </div>

        <div className="help-modal__section">
          <h3>Studying for the German Wine Scholar?</h3>
          <p>The GWS course material predates June 2025 and is now outdated for Württemberg, and the exam will still test the old names. Keep this mapping in mind when moving between the study guide and this map:</p>
          <p>Remstal-Stuttgart (in your materials) is Remstal on this map.</p>
          <p>Oberer Neckar (in your materials) is Neckar-Alb on this map.</p>
          <p>They cover the same vineyards — answer with the old names in the exam, but expect the new names on labels and in any post-2025 German source.</p>
        </div>

        <div className="help-modal__section">
          <h3>Sources</h3>
          <ul className="help-modal__links">
            <li><a href="https://www.weinbauverband-wuerttemberg.de/seite/698511/merkbl%C3%A4tter-und-downloads.html" target="_blank" rel="noopener noreferrer">Weinbauverband Württemberg: Merkblätter und Downloads</a></li>
            <li><a href="https://www.rebeundwein.de/aktuelles/news/article-8206156-198999/herkunft-entscheidet-ueber-die-qualitaet-.html" target="_blank" rel="noopener noreferrer">Rebe &amp; Wein: Herkunft entscheidet über die Qualität</a></li>
            <li><a href="https://de.wikipedia.org/wiki/W%C3%BCrttemberg_(Weinbaugebiet)" target="_blank" rel="noopener noreferrer">Wikipedia: Württemberg (Weinbaugebiet)</a></li>
          </ul>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function UnassignedModal({ onClose }) {
  return createPortal(
    <div className="help-overlay" onClick={onClose}>
      <div className="help-modal" onClick={e => e.stopPropagation()}>
        <button className="help-modal__close" onClick={onClose}>×</button>
        <h2 className="help-modal__title">Unassigned Vineyards</h2>

        <div className="help-modal__section">
          <h3>What are these?</h3>
          <p>These vineyards are not currently assigned to any of the 13 official Anbaugebiete. This may be because:</p>
          <p>They are designated as grape-growing land in OpenStreetMap but fall outside the boundaries of an official wine region, or they have been misclassified during the import process and need manual review.</p>
        </div>

        <div className="help-modal__section">
          <h3>What should I do?</h3>
          <p>If you have edit access, you can assign these vineyards to the correct region using the lasso tool or by clicking individual vineyards. Some may genuinely belong outside the 13 regions and can be left as-is.</p>
        </div>
      </div>
    </div>,
    document.body
  );
}

function HelpModal({ onClose, onNavigateExample }) {
  // 'main' | 'impressum' — the impressum is a state of this popup, but it
  // keeps its own URL: entering pushes /impressum, leaving restores /.
  const [view, setView] = useState(() =>
    window.location.pathname.startsWith('/impressum') ? 'impressum' : 'main');
  // React reuses the scroll div across views, so the main view's scroll
  // position would carry over — always open the impressum at the top
  const impressumScrollRef = useRef(null);
  useEffect(() => {
    if (view === 'impressum') impressumScrollRef.current?.scrollTo(0, 0);
  }, [view]);

  const enterImpressum = () => {
    setView('impressum');
    if (!window.location.pathname.startsWith('/impressum')) {
      window.history.pushState(null, '', '/impressum');
    }
  };
  const leaveImpressum = () => {
    setView('main');
    if (window.location.pathname.startsWith('/impressum')) {
      window.history.replaceState(null, '', '/');
    }
  };
  const close = () => {
    if (window.location.pathname.startsWith('/impressum')) {
      window.history.replaceState(null, '', '/');
    }
    onClose();
  };

  // browser back/forward keeps the view in step with the URL
  useEffect(() => {
    const onPop = () =>
      setView(window.location.pathname.startsWith('/impressum') ? 'impressum' : 'main');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (view === 'impressum') {
    return createPortal(
      <div className="help-overlay" onClick={close}>
        <div className="help-modal help-modal--sticky" onClick={e => e.stopPropagation()}>
          <div className="help-modal__sticky-header help-modal__sticky-header--impressum">
            <button className="detail__back" onClick={leaveImpressum} aria-label="Back">←</button>
            <h2 className="help-modal__title">Impressum</h2>
            <button className="help-modal__close" onClick={close}>×</button>
          </div>
          <div className="help-modal__scroll" ref={impressumScrollRef}>
            <ImpressumContent />
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="help-overlay" onClick={close}>
      <div className="help-modal help-modal--sticky" onClick={e => e.stopPropagation()}>
        <div className="help-modal__sticky-header">
          <h2 className="help-modal__title help-modal__title--cheers">
            {HELP_TITLE}
            <svg className="help-modal__cheers" viewBox="0 0 32 26" width="27" height="22" role="img" aria-label="Cheers">
              <g transform="rotate(16 8 23)">
                <path d="M3.5 1 h9.5 l-1.2 7.4 a3.6 3.9 0 0 1 -7.1 0 Z" fill="#f2c437" />
                <path d="M3.5 1 h9.5 l-0.35 2.2 h-8.8 Z" fill="#f7dc85" />
                <rect x="7.65" y="12.1" width="1.3" height="7.2" rx="0.6" fill="#c3cdd9" />
                <rect x="4.6" y="19.6" width="7.4" height="1.9" rx="0.95" fill="#c3cdd9" />
              </g>
              <g transform="translate(32 0) scale(-1 1) rotate(16 8 23)">
                <path d="M3.5 1 h9.5 l-1.2 7.4 a3.6 3.9 0 0 1 -7.1 0 Z" fill="#f2c437" />
                <path d="M3.5 1 h9.5 l-0.35 2.2 h-8.8 Z" fill="#f7dc85" />
                <rect x="7.65" y="12.1" width="1.3" height="7.2" rx="0.6" fill="#c3cdd9" />
                <rect x="4.6" y="19.6" width="7.4" height="1.9" rx="0.95" fill="#c3cdd9" />
              </g>
            </svg>
          </h2>
          <button className="help-modal__close" onClick={close}>×</button>
        </div>

        <div className="help-modal__scroll">
          <HelpContent onNavigateExample={onNavigateExample} onClose={close} />
        </div>

        <div className="help-modal__sticky-footer">
          {/* buttons ride above the disclaimer sentence */}
          <div className="help-modal__footer-links">
            <a href="https://www.linkedin.com/in/mike-rodden/" target="_blank" rel="noopener noreferrer" className="help-modal__footer-btn" aria-label="LinkedIn">
              <svg width="13" height="13" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
            <a href="/impressum" className="help-modal__footer-btn" onClick={(e) => { e.preventDefault(); enterImpressum(); }}>Impressum</a>
          </div>
          <p className="help-modal__disclaimer">
            This project is for personal educational use only and will not be monetized in any way.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

const fmtHa = (n) => Math.round(n).toLocaleString('en-US');

// Variety chips sized by planted share, same red/white pill format as the
// vineyard view. Top 7 in the cloud (custom percent tooltip); the long tail
// hides behind a one-way "+x more" that lists names with percents.
function VarietyCloud({ varieties }) {
  const [expanded, setExpanded] = useState(false);
  if (!varieties || varieties.length === 0) return null;
  const top = varieties.slice(0, 7);
  const rest = varieties.slice(7);
  const maxPct = top[0].pct || 1;
  // extras render as real chips, one step smaller than the smallest
  // naturally-sized one; names may clip, so their tooltip carries both
  const lastSize = 0.58 + 0.75 * ((top[top.length - 1].pct || 0) / maxPct);
  const tailSize = Math.max(0.44, lastSize - 0.1).toFixed(2);
  return (
    <>
      <div className="variety-cloud">
        {top.map(v => (
          <Tip
            key={v.name}
            as="span"
            tip={`${v.pct}%`}
            className={`detail__grape-chip detail__grape-chip--${v.color === 'red' ? 'red' : v.color === 'white' ? 'white' : 'unknown'} detail__grape-chip--cloud`}
            style={{ fontSize: `${(0.58 + 0.75 * (v.pct / maxPct)).toFixed(2)}rem` }}
          >
            {v.name}
          </Tip>
        ))}
        {expanded && rest.map(v => (
          <Tip
            key={v.name}
            as="span"
            tip={`${v.name} | ${v.pct}%`}
            className={`detail__grape-chip detail__grape-chip--${v.color === 'red' ? 'red' : v.color === 'white' ? 'white' : 'unknown'} detail__grape-chip--cloud detail__grape-chip--tail`}
            style={{ fontSize: `${tailSize}rem` }}
          >
            {v.name}
          </Tip>
        ))}
        {!expanded && rest.length > 0 && (
          <button className="variety-cloud__more" onClick={() => setExpanded(true)}>
            +{rest.length} more
          </button>
        )}
      </div>
    </>
  );
}

// Horizontal bars of planted hectares per bereich, tinted with each
// bereich's map color so the chart reads against the legend.
function BereichHaChart({ region, bereiche, rowTip }) {
  if (!bereiche || bereiche.length === 0) return null;
  const max = bereiche[0].ha || 1;
  return (
    <div className="ha-chart">
      {bereiche.map(b => {
        const tip = rowTip ? rowTip(b) : null;
        const inner = (<>
          <div className="ha-chart__top">
            <span className="ha-chart__name">{b.name}</span>
            <span className="ha-chart__val">{fmtHa(b.ha)} ha</span>
          </div>
          <div className="ha-chart__track">
            <div className="ha-chart__bar" style={{ width: `${(100 * b.ha / max).toFixed(1)}%`, background: colorForBereich(b.name, region) }} />
          </div>
        </>);
        return tip
          ? <Tip key={b.name} tip={tip} as="div" className="ha-chart__row">{inner}</Tip>
          : <div key={b.name} className="ha-chart__row">{inner}</div>;
      })}
    </div>
  );
}

// Mapped-area block (see scripts/compute_bereich_areas.py): the union
// area of this app's own polygons, total first as a number, then the
// per-bereich bars. Bars carry the official Landesamt planted-hectare
// figure in their tooltip where one exists, for comparison.
function MappedAreaBlock({ region }) {
  const [open, setOpen] = useState(true);
  const data = COMPUTED_AREAS[region];
  if (!data) return null;
  const officialTip = (b) => {
    const o = BEREICH_STATS[region]?.[b.name];
    return o?.totalHa
      ? `Official Statistisches Landesamt figure from ${o.year}: ${fmtHa(o.totalHa)} ha`
      : null;
  };
  return (<>
    <h4
      className="detail__read-heading detail__oldvines-heading sidebar__stats-sub"
      style={{ cursor: 'pointer' }}
      onClick={(e) => {
        const el = e.currentTarget;
        setOpen(o => {
          const next = !o;
          if (next) {
            requestAnimationFrame(() => requestAnimationFrame(() => {
              el.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }));
          }
          return next;
        });
      }}
    >
      Planted Area by Bereich
      <svg className={`sidebar__expand-chevron ${open ? 'sidebar__expand-chevron--up' : ''}`} width="10" height="7" viewBox="0 0 10 7"><polyline points="1,1 5,6 9,1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </h4>
    {open && (<>
      {/* the region total leads as a number, not a bar — rendered in the
          chart's own row format so the bold value sits directly above the
          column of bereich numbers */}
      <div className="ha-chart">
        <div className="ha-chart__row">
          <div className="ha-chart__top">
            <span className="ha-chart__name">Total mapped</span>
            <Tip tip="The total area of this app's mapped vineyards" as="span" className="ha-chart__val ha-chart__val--total">{fmtHa(data.totalHa)} ha</Tip>
          </div>
        </div>
      </div>
      <BereichHaChart region={region} bereiche={data.bereiche} rowTip={officialTip} />
    </>)}
  </>);
}

export default function Sidebar({ user, signIn, signOut, hoveredRegion, onHoverRegion, onHoverVillage, onHoverVineyard, regionBackDisabled, onExitFlythrough, lassoArmed, setLassoArmed, mode, loading, onSelectRegion, onSetMode, focusBereich, onFocusBereich, onSelectVineyard, onSelectVillage, onSelectProducer, villageEditMode, setVillageEditMode, labelEditMode, setLabelEditMode, regionLabelEditMode, setRegionLabelEditMode, geoLayer, geoLabelEditMode, setGeoLabelEditMode, riverLabelEditMode, setRiverLabelEditMode, mtnIconEditMode, setMtnIconEditMode, helpOpen, setHelpOpen, onSearchResult, onStartDraw, showVillages, toggleVillages, showProducers, toggleProducers, showLabels, toggleLabels, mapZoom, showAllVineyards, toggleAllVineyards, producersLayer, keyProducers, onSelectKeyProducer, onHoverKeyProducer, onProducerFilter }) {
  const activeRegion = mode.kind === 'region' ? mode.name : null;
  const [bereicheByRegion, setBereicheByRegion] = useState({});
  const [unassignedNotice, setUnassignedNotice] = useState(false);
  const [frankenNotice, setFrankenNotice] = useState(false);
  const [wurttembergNotice, setWurttembergNotice] = useState(false);
  const [statsOpen, setStatsOpen] = useState(true);
  const [villagesOpen, setVillagesOpen] = useState(true);
  // Easter-egg title language: the overview flag silently toggles it
  const [germanTitle, setGermanTitle] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(true);
  // Key Producers view: simple name-or-grape filter over the lists.
  // Name matching folds umlauts both ways; grapes also match through
  // their synonym group (pinot gris/grigio finds Grauburgunder).
  const [producerQuery, setProducerQuery] = useState('');
  useEffect(() => { setProducerQuery(''); }, [producersLayer]);
  const producerMatches = (p, q) => !q
    || foldedIncludes(p.name, q)
    || (Array.isArray(p.grapes) && p.grapes.some(g => grapeMatchesQuery(g, q)));
  // The narrowed set reflects on the map: matched ids reported up so
  // their markers grow and the rest mute (null = no active filter)
  useEffect(() => {
    if (!onProducerFilter) return;
    const q = producerQuery.trim().toLowerCase();
    if (!producersLayer || !q) { onProducerFilter(null); return; }
    onProducerFilter(new Set(
      (keyProducers ?? []).filter(p => producerMatches(p, q)).map(p => p.id)
    ));
    // producerMatches is a stable pure closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producerQuery, keyProducers, producersLayer, onProducerFilter]);
  const [notableOpen, setNotableOpen] = useState(true);
  const [producersOpen, setProducersOpen] = useState(true);
  // expanding any section scrolls it to the top of the sidebar (or as
  // close as the remaining scroll range allows)
  const toggleSection = (e, setter) => {
    const el = e.currentTarget.closest('.sidebar__section') ?? e.currentTarget;
    setter(o => {
      const next = !o;
      if (next) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }));
      }
      return next;
    });
  };

  useEffect(() => {
    let cancel = false;
    supabase.from('bereiche').select('name, anbaugebiet').then(({ data }) => {
      if (cancel) return;
      const byRegion = {};
      for (const b of data ?? []) {
        const region = b.anbaugebiet;
        if (!byRegion[region]) byRegion[region] = [];
        byRegion[region].push(b.name);
      }
      // Sort each region's bereiche alphabetically
      for (const k of Object.keys(byRegion)) byRegion[k].sort();
      setBereicheByRegion(byRegion);
    });
    return () => { cancel = true; };
  }, []);

  const bereiche = activeRegion ? (bereicheByRegion[activeRegion] ?? bereicheFor(activeRegion)) : [];

  // Title-anchored region/bereich pickers: the header's region name and
  // bereich sub open scrollable option lists — one at a time, closed on
  // outside click, on pick, or when the view changes under them.
  const [openPick, setOpenPick] = useState(null); // null | 'region' | 'bereich'
  const headerRef = useRef(null);
  useEffect(() => { setOpenPick(null); }, [activeRegion, focusBereich]);
  useEffect(() => {
    if (!openPick) return;
    const onDown = (e) => { if (headerRef.current && !headerRef.current.contains(e.target)) setOpenPick(null); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openPick]);

  // Fetch producers whenever a region is open: bereich-scoped while one
  // is focused, region-wide otherwise (p_bereich null = whole region —
  // multi-bereich German regions used to fetch nothing at region level,
  // hiding the Producers section entirely, e.g. Württemberg)
  const activeBereichForFetch = focusBereich || (bereiche.length === 1 ? bereiche[0] : null);
  useEffect(() => {
    if (!activeRegion) { setProducers([]); return; }
    let cancel = false;
    const params = { p_anbaugebiet: activeRegion, p_bereich: activeBereichForFetch };
    supabase.rpc('producers_by_bereich', params)
      .then(({ data }) => {
        if (!cancel) setProducers(Array.isArray(data) ? data : []);
      })
      .catch(e => console.error('[Sidebar] producers:', e.message));
    return () => { cancel = true; };
  }, [activeRegion, activeBereichForFetch]);

  const [notableVineyards, setNotableVineyards] = useState([]);
  const [villages, setVillages] = useState([]);
  const [producers, setProducers] = useState([]);

  useEffect(() => {
    if (!activeRegion) { setNotableVineyards([]); setVillages([]); return; }
    let cancel = false;
    supabase
      .from('vineyards')
      .select('id, canonical_name_de, full_label_name, einzellage, bereich')
      // nested AVAs: child-region notables ride the parent view too
      .or(`anbaugebiet.eq."${activeRegion}",parent_ava.eq."${activeRegion}"`)
      .eq('notable', true)
      .order('canonical_name_de')
      .then(({ data }) => {
        if (!cancel) setNotableVineyards(data ?? []);
      })
      .catch(e => console.error('[Sidebar] notable vineyards:', e.message));
    supabase
      .rpc('villages_by_region', { p_region: activeRegion })
      .then(({ data }) => {
        if (!cancel) setVillages(data ?? []);
      })
      .catch(e => console.error('[Sidebar] villages:', e.message));
    return () => { cancel = true; };
  }, [activeRegion]);


  return (
    <aside className={`sidebar ${mode.kind === 'regions' ? 'sidebar--regions' : ''}`}>
      <header ref={headerRef} className={`sidebar__header ${mode.kind !== 'regions' ? 'sidebar__header--collapsible' : ''}`}>
        {/* small attribution line riding above the big overview title */}
        {!activeRegion && !producersLayer && <p className="sidebar__sub sidebar__sub--above">Mike's</p>}
        <h1 className="sidebar__title">
          {/* back navigation lives in the top-left map bar; the header only
              keeps the fly-through exit button */}
          {activeRegion && regionBackDisabled && (
            <Tip tip="Back to 2D map" as="span">
              <button
                className="detail__back"
                onClick={onExitFlythrough}
                aria-label="Back to 2D map"
              ><ExitIcon size={13} style={{ display: 'block', margin: 'auto' }} /></button>
            </Tip>
          )}
          {activeRegion ? (
            <span
              className="sidebar__title-text sidebar__title-pick"
              onClick={() => setOpenPick(p => (p === 'region' ? null : 'region'))}
            >
              {activeRegion}
              <svg className={`sidebar__title-caret ${openPick === 'region' ? 'sidebar__title-caret--up' : ''}`} width="11" height="8" viewBox="0 0 10 7"><polyline points="1,1 5,6 9,1" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </span>
          ) : producersLayer ? (
            <span className="sidebar__title-text">Key Producers</span>
          ) : (
            <span className="sidebar__title-text sidebar__title-text--brand">
              {germanTitle && ATLAS.altTitle ? ATLAS.altTitle : ATLAS.name}
              {/* the flag-grape is a hidden toggle — no affordance, but a
                  click flips the title language (atlases without an
                  alternate title just keep their name). Dedicated
                  tight-cropped asset: the favicon files carry the full
                  padded canvas and render tiny + soft at this size */}
              <img className="sidebar__flag" onClick={ATLAS.altTitle ? () => setGermanTitle(t => !t) : undefined} src={ATLAS.logoUrl} width="32" height="39" alt="" role="img" aria-label={ATLAS.country} />
            </span>
          )}
          {!activeRegion && producersLayer && <span className="sidebar__sub sidebar__sub--inline">{ATLAS.country}</span>}
          {activeRegion && (bereiche.length > 1 ? (
            <span
              className="sidebar__sub sidebar__sub--inline sidebar__title-pick"
              onClick={() => setOpenPick(p => (p === 'bereich' ? null : 'bereich'))}
            >
              {focusBereich || 'Full region'}
              {activeRegion === 'Franken' && (
                <button className="legend-list__info legend-list__info--warn" onClick={(e) => { e.stopPropagation(); setFrankenNotice(true); }} title="About Franken's Bereiche"><WarnIcon size={15} /></button>
              )}
              {activeRegion === 'Württemberg' && (
                <button className="legend-list__info legend-list__info--warn" onClick={(e) => { e.stopPropagation(); setWurttembergNotice(true); }} title="About Württemberg's Bereiche"><WarnIcon size={15} /></button>
              )}
              <svg className={`sidebar__title-caret ${openPick === 'bereich' ? 'sidebar__title-caret--up' : ''}`} width="9" height="6" viewBox="0 0 10 7"><polyline points="1,1 5,6 9,1" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </span>
          ) : bereiche.length === 1 ? (
            <Tip tip={`${activeRegion} has only one Bereich`} as="span" className="sidebar__sub sidebar__sub--inline">{bereiche[0]}</Tip>
          ) : (
            /* atlases without sub-divisions (michigan): plain tier label */
            <span className="sidebar__sub sidebar__sub--inline">{ATLAS.terms.region}</span>
          ))}
        </h1>
        {openPick && (
          <div className={`sidebar__pick-panel sidebar__pick-panel--${openPick}`}>
            <ul className="legend-list">
              {(openPick === 'region' ? Object.keys(REGION_COLORS) : ['All Bereiche', ...bereiche]).map(name => {
                const selected = openPick === 'region'
                  ? name === activeRegion
                  : name === (focusBereich ?? 'All Bereiche');
                return (
                  <li
                    key={name}
                    className="legend-list__item"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setOpenPick(null);
                      if (selected) return;
                      if (openPick === 'region') onSelectRegion(name);
                      else onFocusBereich(name === 'All Bereiche' ? null : name);
                    }}
                  >
                    <span className="legend-list__swatch" style={{ visibility: 'hidden' }} />
                    {selected && (
                      <svg className="legend-list__pin legend-list__pin--name" viewBox="0 0 24 24" width="20" height="20" fill="#a8a29e"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/></svg>
                    )}
                    <span>{name}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </header>

      <div className="sidebar__body">
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} onNavigateExample={() => {
        // Showcase vineyard: Uhlen Blaufüsser Lay (custom/-116), Terrassenmosel.
        // Routed through the search-result flow so the app switches to the
        // Mosel, selects it, zooms in and opens the detail sidebar.
        onSearchResult?.({
          kind: 'vineyard',
          id: '821c42c0-034f-4e2b-a5c6-94ad34925db7',
          label: 'Uhlen Blaufüsser Lay',
          anbaugebiet: 'Mosel',
          latitude: 50.3168673671738,
          longitude: 7.49461876502482,
        });
      }} />}
      {unassignedNotice && <UnassignedModal onClose={() => setUnassignedNotice(false)} />}
      {frankenNotice && <FrankenModal onClose={() => setFrankenNotice(false)} />}
      {wurttembergNotice && <WurttembergModal onClose={() => setWurttembergNotice(false)} />}

      {/* While the mode's data loads, the header already names the new view —
          the body shows shimmer instead of stale-looking content. */}
      {loading && (
        <div aria-hidden="true">
          {[34, 88, 72, 84, 60, 30, 86, 66, 78, 52, 82, 44].map((w, i) => (
            <span key={i} className="skeleton-line" style={{ width: `${w}%` }} />
          ))}
        </div>
      )}
      <div style={loading ? { display: 'none' } : { display: 'contents' }}>

      {/* Key Producers layer (overview): per-region sections of every
          producer, replacing the region legend, filtered by the simple
          name-or-grape search. A region whose producers all fall out
          hides entirely. */}
      {!activeRegion && producersLayer && (
        <section className="sidebar__section sidebar__psearch">
          <div className="sidebar__producer-search-wrap">
            <input
              className="sidebar__producer-search"
              type="text"
              placeholder="Search producer or grape…"
              value={producerQuery}
              onChange={(e) => setProducerQuery(e.target.value)}
            />
            {producerQuery && (
              <button type="button" className="search-bar__close search-bar__close--sm" onClick={() => setProducerQuery('')} aria-label="Clear search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          {/* second line: which grape varieties the typing currently
              selects for, as the app's grape chips (canonical spelling,
              synonyms collapsed) */}
          {(() => {
            const q = producerQuery.trim();
            if (!q) return null;
            const seen = new Set();
            const hits = [];
            for (const p of keyProducers ?? []) {
              for (const g of (Array.isArray(p.grapes) ? p.grapes : [])) {
                if (!grapeMatchesQuery(g, q)) continue;
                const disp = grapeDisplay(g);
                if (seen.has(disp.toLowerCase())) continue;
                seen.add(disp.toLowerCase());
                hits.push(disp);
              }
            }
            if (hits.length === 0) return null;
            hits.sort((a, b) => a.localeCompare(b));
            return (
              <div className="sidebar__grape-hits">
                {hits.map(g => (
                  <button
                    key={g}
                    className={`detail__grape-chip detail__grape-chip--${grapeKind(g) === 'red' ? 'red' : grapeKind(g) === 'white' ? 'white' : 'unknown'} sidebar__grape-hit`}
                    onClick={() => setProducerQuery(g)}
                    title={`Filter for ${g}`}
                  >{g}</button>
                ))}
              </div>
            );
          })()}
        </section>
      )}
      {!activeRegion && producersLayer && (() => {
        const q = producerQuery.trim().toLowerCase();
        return Object.keys(REGION_COLORS).map(region => {
        const ps = (keyProducers ?? []).filter(p => p.anbaugebiet === region && producerMatches(p, q));
        if (ps.length === 0) return null;
        return (
          <section className="sidebar__section" key={`kp-${region}`}>
            <h3 className="sidebar__h3">{region}</h3>
            <ul
              className="legend-list legend-list--top"
              onMouseLeave={isMobile() ? undefined : () => onHoverKeyProducer?.(null)}
            >
              {ps.map(p => (
                <li
                  key={p.id}
                  className="legend-list__item"
                  onClick={() => onSelectKeyProducer?.(p)}
                  onMouseEnter={isMobile() ? undefined : () => onHoverKeyProducer?.(p.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* favorite marker, hung in the list's left gutter */}
                  {isFavoriteProducer(p) && (
                    <Tip tip="One of Mike's favorite German producers" as="span" className="producer-fav-star"><StarIcon size={16} /></Tip>
                  )}
                  <span className="notable-star-sm" style={{ width: 17 }}><BottleIcon /></span>
                  <span>{p.name}</span>
                </li>
              ))}
            </ul>
          </section>
        );
        });
      })()}

      {/* Overview/unassigned only: the full region legend (map hover sync).
          In region view the header's title-anchored pickers cover region
          and bereich switching. */}
      {!activeRegion && !producersLayer && (
      <section className="sidebar__section">
        <h3 className="sidebar__h3">Regions</h3>
        <ul
          className="legend-list legend-list--grid legend-list--even legend-list--regions"
          onMouseLeave={!isMobile() ? () => onHoverRegion?.(null) : undefined}
        >
          {Object.keys(REGION_COLORS).map(name => {
            const color = REGION_COLORS[name];
            const isMuted = mode.kind === 'unassigned';
            return (
              <li
                key={name}
                className={`legend-list__item ${isMuted ? 'legend-list__item--muted' : ''} ${hoveredRegion === name ? 'legend-list__item--map-hover' : ''} ${hoveredRegion && hoveredRegion !== name ? 'legend-list__item--hover-dim' : ''}`}
                onClick={() => onSelectRegion(name)}
                onMouseEnter={!isMobile() ? () => onHoverRegion?.(name) : undefined}
                style={{ cursor: 'pointer' }}
              >
                {/* the vine glyph in the region's color instead of a dot */}
                <span className="notable-star-sm" style={{ color: isMuted ? '#ccc' : color }}><VineIcon size={15.4} /></span>
                <span>{name}</span>
              </li>
            );
          })}
          {user && (
            <li
              className={`legend-list__item ${mode.kind !== 'unassigned' ? 'legend-list__item--muted' : ''}`}
              onClick={() => {
                onSetMode({ kind: 'unassigned' });
                if (!sessionStorage.getItem('unassignedSeen')) {
                  sessionStorage.setItem('unassignedSeen', '1');
                  setUnassignedNotice(true);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <span
                className="legend-list__swatch"
                style={{ background: mode.kind === 'unassigned' ? '#1c1c1c' : '#ccc', borderColor: mode.kind === 'unassigned' ? '#1c1c1c' : '#ccc' }}
              />
              <span>Unassigned</span>
              <button className="legend-list__info" onClick={(e) => { e.stopPropagation(); setUnassignedNotice(true); }} title="What are unassigned vineyards?">i</button>
            </li>
          )}
        </ul>
      </section>
      )}

      {mode.kind === 'regions' && !user && !producersLayer && (
        <section className="sidebar__section">
          <h3 className="sidebar__h3">How to use</h3>
          <p className="sidebar__howto">{ATLAS.id === 'germany'
            ? 'Click a region on the map or in the list above to explore its vineyards, bereiche, and villages.'
            : `Click an ${ATLAS.terms.region} on the map or in the list above to explore its vineyards, wineries, and wine towns.`}</p>
          <p className="sidebar__howto">
            For edit access or questions, please reach out to Mike on{' '}
            <a href="https://www.linkedin.com/in/mike-rodden/" target="_blank" rel="noopener noreferrer" className="sidebar__linkedin">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#0a66c2" style={{ verticalAlign: '-0.15em' }}><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
          </p>
        </section>
      )}

      {/* Planting statistics — bereich view gets its variety cloud + notes,
          the full-region view gets the aggregate cloud + per-bereich chart */}
      {/* long-form AVA prose (atlas content; no German entries exist so
          the section never renders there). Collapsed by default. */}
      {activeRegion && REGION_CONTENT[activeRegion] && (
        <section className="sidebar__section">
          <h3 className="sidebar__h3" onClick={(e) => toggleSection(e, setAboutOpen)} style={{ cursor: 'pointer' }}>
            About
            <svg className={`sidebar__expand-chevron ${aboutOpen ? 'sidebar__expand-chevron--up' : ''}`} width="10" height="7" viewBox="0 0 10 7"><polyline points="1,1 5,6 9,1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </h3>
          {aboutOpen && (
            <div className="sidebar__about">
              {REGION_CONTENT[activeRegion].growingConditions && (<>
                <h4 className="detail__read-heading">Growing conditions</h4>
                <p className="sidebar__about-text">{REGION_CONTENT[activeRegion].growingConditions}</p>
              </>)}
              {REGION_CONTENT[activeRegion].history && (<>
                <h4 className="detail__read-heading">History</h4>
                <p className="sidebar__about-text">{REGION_CONTENT[activeRegion].history}</p>
              </>)}
            </div>
          )}
        </section>
      )}

      {activeRegion && (() => {
        const statsBereich = focusBereich || (bereiche.length === 1 ? bereiche[0] : null);
        const bStats = statsBereich ? BEREICH_STATS[activeRegion]?.[statsBereich] : null;
        const rStats = !statsBereich ? REGION_STATS[activeRegion] : null;
        if (bStats && bStats.varieties.length > 0) {
          return (
            <section className="sidebar__section" key={`vars-${statsBereich}`}>
              <h3 className="sidebar__h3" onClick={(e) => toggleSection(e, setStatsOpen)} style={{ cursor: 'pointer' }}>
                {bereiche.length === 1 ? 'Grapes' : 'Statistics'}
                <svg className={`sidebar__expand-chevron ${statsOpen ? 'sidebar__expand-chevron--up' : ''}`} width="10" height="7" viewBox="0 0 10 7"><polyline points="1,1 5,6 9,1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </h3>
              {statsOpen && (<>
                <VarietyCloud varieties={bStats.varieties} />
                {/* single-bereich regions: this section IS the region view,
                    so the mapped-area block lives here (a focused bereich
                    in a multi-bereich region deliberately doesn't) */}
                {bereiche.length === 1 && <MappedAreaBlock region={activeRegion} />}
              </>)}
            </section>
          );
        }
        if (rStats) {
          return (
            <section className="sidebar__section" key={`stats-${activeRegion}`}>
              <h3 className="sidebar__h3" onClick={(e) => toggleSection(e, setStatsOpen)} style={{ cursor: 'pointer' }}>
                Statistics
                <svg className={`sidebar__expand-chevron ${statsOpen ? 'sidebar__expand-chevron--up' : ''}`} width="10" height="7" viewBox="0 0 10 7"><polyline points="1,1 5,6 9,1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </h3>
              {statsOpen && (<>
                <VarietyCloud varieties={rStats.varieties} />
                {/* the official Plantings-by-Bereich chart retired in favor
                    of Mapped Area (official figures live in its row tips) */}
                <MappedAreaBlock region={activeRegion} />
              </>)}
            </section>
          );
        }
        return null;
      })()}

      {activeRegion && (() => {
        const bereichFilter = focusBereich && bereiche.length > 1 ? focusBereich : null;

        const filteredNotable = bereichFilter
          ? notableVineyards.filter(v => v.bereich === bereichFilter)
          : notableVineyards;

        const filteredVillages = bereichFilter
          ? villages.filter(v => v.bereich === bereichFilter)
          : villages;

        // Producers already filtered by bereich via RPC
        const filteredProducers = producers;

        const seen = new Set();
        const dedupedNotable = filteredNotable.filter(v => {
          const name = v.canonical_name_de || v.full_label_name || v.einzellage || 'Unnamed';
          if (seen.has(name)) return false;
          seen.add(name);
          return true;
        });

        return (<>
          {filteredVillages.length > 0 && (
            <section className="sidebar__section">
              <h3 className="sidebar__h3" onClick={(e) => toggleSection(e, setVillagesOpen)} style={{ cursor: 'pointer' }}>
                Key Villages
                <Tip tip={`${showVillages ? 'Hide' : 'Show'} key villages on the map`} as="span" className="sidebar__eye-wrap">
                  <button
                    className={`sidebar__eye ${showVillages ? 'sidebar__eye--on' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleVillages?.(); }}
                    aria-pressed={!!showVillages}
                    aria-label="Toggle key villages on the map"
                  >{showVillages ? <EyeIcon size={14} /> : <EyeOffIcon size={14} />}</button>
                </Tip>
                <svg className={`sidebar__expand-chevron ${villagesOpen ? 'sidebar__expand-chevron--up' : ''}`} width="10" height="7" viewBox="0 0 10 7"><polyline points="1,1 5,6 9,1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </h3>
              {villagesOpen && (
                <ul className="legend-list legend-list--grid" onMouseLeave={isMobile() ? undefined : () => onHoverVillage?.(null)}>
                  {filteredVillages.map(v => (
                    <li
                      key={v.id}
                      className="legend-list__item"
                      onClick={() => onSelectVillage(v)}
                      onMouseEnter={isMobile() ? undefined : () => onHoverVillage?.(v)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="notable-star-sm" style={{ color: '#d97706' }}><HouseIcon size={12} /></span>
                      <span>{v.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {dedupedNotable.length > 0 && (
            <section className="sidebar__section">
              <h3 className="sidebar__h3" onClick={(e) => toggleSection(e, setNotableOpen)} style={{ cursor: 'pointer' }}>
                Notable Vineyards
                <Tip tip={mapZoom < 12 ? 'Zoom in further to use this toggle' : `${showLabels ? 'Hide' : 'Show'} GWS & GG labels on the map`} as="span" className="sidebar__eye-wrap">
                  <button
                    className={`sidebar__eye ${showLabels && mapZoom >= 12 ? 'sidebar__eye--on' : ''} ${mapZoom < 12 ? 'sidebar__eye--disabled' : ''}`}
                    onClick={(e) => { e.stopPropagation(); if (mapZoom >= 12) toggleLabels?.(); }}
                    aria-pressed={!!showLabels}
                    aria-label="Toggle GWS & GG labels on the map"
                  >{showLabels && mapZoom >= 12 ? <EyeIcon size={14} /> : <EyeOffIcon size={14} />}</button>
                </Tip>
                <Tip tip={showAllVineyards ? 'Filter map for only notable and VDP vineyards' : 'Remove filter & show all vineyards'} as="span" className="sidebar__eye-wrap">
                  <button
                    className={`sidebar__eye ${!showAllVineyards ? 'sidebar__eye--on' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleAllVineyards?.(); }}
                    aria-pressed={!showAllVineyards}
                    aria-label="Filter map for only notable and VDP vineyards"
                  ><FilterIcon size={13} /></button>
                </Tip>
                <svg className={`sidebar__expand-chevron ${notableOpen ? 'sidebar__expand-chevron--up' : ''}`} width="10" height="7" viewBox="0 0 10 7"><polyline points="1,1 5,6 9,1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </h3>
              {notableOpen && (
                <ul className="legend-list legend-list--grid" onMouseLeave={isMobile() ? undefined : () => onHoverVineyard?.(null)}>
                  {dedupedNotable.map(v => (
                    <li
                      key={v.id}
                      className="legend-list__item"
                      onClick={() => onSelectVineyard(v.id)}
                      onMouseEnter={isMobile() ? undefined : () => onHoverVineyard?.(v.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="notable-star-sm"><VineIcon /></span>
                      <span>{v.canonical_name_de || v.full_label_name || v.einzellage || 'Unnamed'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {filteredProducers.length > 0 && (
            <section className="sidebar__section">
              <h3 className="sidebar__h3" onClick={(e) => toggleSection(e, setProducersOpen)} style={{ cursor: 'pointer' }}>
                Producers
                <Tip tip={`${showProducers ? 'Hide' : 'Show'} producers on the map`} as="span" className="sidebar__eye-wrap">
                  <button
                    className={`sidebar__eye ${showProducers ? 'sidebar__eye--on' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleProducers?.(); }}
                    aria-pressed={!!showProducers}
                    aria-label="Toggle producers on the map"
                  >{showProducers ? <EyeIcon size={14} /> : <EyeOffIcon size={14} />}</button>
                </Tip>
                <svg className={`sidebar__expand-chevron ${producersOpen ? 'sidebar__expand-chevron--up' : ''}`} width="10" height="7" viewBox="0 0 10 7"><polyline points="1,1 5,6 9,1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </h3>
              {producersOpen && (
                <ul className="legend-list legend-list--grid legend-list--top">
                  {filteredProducers.map(p => (
                    <li
                      key={p.id}
                      className="legend-list__item"
                      onClick={() => onSelectProducer(p)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* favorite marker, hung in the list's left gutter */}
                      {isFavoriteProducer(p) && (
                        <Tip tip="One of Mike's favorite German producers" as="span" className="producer-fav-star"><StarIcon size={16} /></Tip>
                      )}
                      <span className="notable-star-sm" style={{ width: 17 }}><BottleIcon /></span>
                      <span>{p.short_name || p.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </>);
      })()}

      {/* no label tools in the producers view — its grey backdrop labels
          are read-only there */}
      {user && !isMobile() && mode.kind === 'regions' && !producersLayer && (
        <section className="sidebar__section">
          <h3 className="sidebar__h3">Tools</h3>
          {geoLayer ? (
            // The Rivers & Mountains view has its own label positions —
            // these tools move those, not the normal overview labels
            <>
              <button
                className={`lasso-toggle ${geoLabelEditMode ? 'is-armed' : ''}`}
                onClick={() => setGeoLabelEditMode(v => !v)}
              >
                {geoLabelEditMode ? '◉ Adjusting geo-view labels…' : '◯ Adjust geo-view labels'}
              </button>
              <button
                className={`lasso-toggle ${riverLabelEditMode ? 'is-armed' : ''}`}
                onClick={() => setRiverLabelEditMode(v => !v)}
              >
                {riverLabelEditMode ? '◉ Adjusting river labels…' : '◯ Adjust river labels'}
              </button>
              <button
                className={`lasso-toggle ${mtnIconEditMode ? 'is-armed' : ''}`}
                onClick={() => setMtnIconEditMode(v => !v)}
              >
                {mtnIconEditMode ? '◉ Adjusting mountain icons…' : '◯ Adjust mountain icons'}
              </button>
            </>
          ) : (
            <button
              className={`lasso-toggle ${regionLabelEditMode ? 'is-armed' : ''}`}
              onClick={() => setRegionLabelEditMode(v => !v)}
            >
              {regionLabelEditMode ? '◉ Adjusting region labels…' : '◯ Adjust region labels'}
            </button>
          )}
        </section>
      )}

      {user && !isMobile() && mode.kind !== 'regions' && (
        <section className="sidebar__section">
          <h3 className="sidebar__h3">Tools</h3>
          <button
            className={`lasso-toggle ${lassoArmed ? 'is-armed' : ''}`}
            onClick={() => setLassoArmed(a => !a)}
            disabled={mode.kind === 'regions'}
          >
            {lassoArmed ? '◉ Selecting…' : '◯ Select multiple vineyards'}
          </button>
          {mode.kind === 'region' && (
            <button
              className={`lasso-toggle ${villageEditMode ? 'is-armed' : ''}`}
              onClick={() => setVillageEditMode(v => !v)}
            >
              {villageEditMode ? '◉ Adjusting villages…' : '◯ Adjust village locations'}
            </button>
          )}
          {mode.kind === 'region' && (
            <button
              className={`lasso-toggle ${labelEditMode ? 'is-armed' : ''}`}
              onClick={() => setLabelEditMode(v => !v)}
            >
              {labelEditMode ? '◉ Adjusting labels…' : '◯ Adjust vineyard labels'}
            </button>
          )}
          {mode.kind === 'region' && (
            <button className="lasso-toggle" onClick={onStartDraw}>
              ✏️ Draw Polygon
            </button>
          )}
        </section>
      )}

      {/* producers view, mobile: the disclaimer scrolls with the list
          (the pinned footer version is display:none there) */}
      {!activeRegion && producersLayer && (
        <p className="sidebar__attr sidebar__producer-disclaimer sidebar__producer-disclaimer--inline">{PRODUCER_DISCLAIMER}</p>
      )}
      </div>
      </div>
      <footer className={`sidebar__footer ${!activeRegion && producersLayer ? 'sidebar__footer--line sidebar__footer--producers' : ''}`}>
        {/* producers view: disclaimer only — no auth UI */}
        {!activeRegion && producersLayer ? (
          <p className="sidebar__attr sidebar__producer-disclaimer">{PRODUCER_DISCLAIMER}</p>
        ) : user ? (
          <div className="auth auth--in">
            <span className="auth__email" title={user.email}>{user.user_metadata?.display_name || user.email}</span>
            <button className="auth__btn" onClick={signOut}>Sign out</button>
          </div>
        ) : (
          !isMobile() && <SignInForm signIn={signIn} />
        )}
      </footer>
    </aside>
  );
}

function SignInForm({ signIn }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (!open) {
    return (
      <button className="auth auth--cta" onClick={() => setOpen(true)}>
        Sign in to edit
      </button>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const { error } = await signIn(email, pw);
    setBusy(false);
    if (error) setErr(error.message);
    else { setOpen(false); setPw(''); }
  };

  return (
    <form className="auth auth--form" onSubmit={submit}>
      <input type="email" placeholder="email" value={email} onChange={e => setEmail(e.target.value)} required />
      <input type="password" placeholder="password" value={pw} onChange={e => setPw(e.target.value)} required />
      {err && <div className="auth__err">{err}</div>}
      <div className="auth__row">
        <button type="button" className="auth__btn auth__btn--ghost" onClick={() => setOpen(false)}>Cancel</button>
        <button type="submit" className="auth__btn" disabled={busy}>{busy ? '…' : 'Sign in'}</button>
      </div>
    </form>
  );
}
